import {html} from 'lit';
import {customElement, property} from 'lit/decorators.js';
import {FlywheelComponent} from '#lit/lib/component.js';
import styles from './style.scss';
import {isEmptyString} from '#lit/utils/index.js';
import {classMap} from 'lit/directives/class-map.js';
import {ref} from 'lit/directives/ref.js';
import {ImmutableFormRegistry} from '#lit/lib/immutable_form.js';

export const COMPONENT_TAG = 'fly-checkbox';

export interface CustomSelectEvent {
  readonly value: string;
  readonly label: string;
  readonly key: string;
  readonly disabled: boolean;
  readonly checked: boolean;
  readonly required: boolean;
  readonly invalid: boolean;
  readonly readonly: boolean;
  readonly name: string;
  readonly $node: HTMLInputElement;
}

export enum CUSTOM_EVENT {
  CHANGE = 'change',
  BLUR = 'blur',
  FOCUS = 'focus',
}

@customElement(COMPONENT_TAG)
export default class FlyCheckbox extends FlywheelComponent {
  @property({type: String})
  key = `${new Date().getTime()}-${Math.random()}`;

  @property({type: String})
  value = '';

  @property({type: String})
  label = '';

  @property({type: String})
  name = '';

  @property({type: Boolean, reflect: true})
  checked = false;

  @property({type: Boolean, reflect: true})
  readonly = false;

  @property({type: Boolean, reflect: true})
  required = false;

  @property({type: Boolean, reflect: true})
  disabled = false;

  @property({type: Boolean, reflect: true})
  invalid = false;

  $node!: HTMLInputElement;

  static get styles() {
    return [styles];
  }

  private dispatchChangeEvent() {
    const evt = {
      key: this.key,
      name: this.name,
      value: this.value,
      label: this.label,
      checked: this.checked,
      disabled: this.disabled,
      required: this.required,
      readonly: this.readonly,
      invalid: this.invalid,
      $node: this.$node,
    };

    this.dispatchEvent(
      new CustomEvent<CustomSelectEvent>(CUSTOM_EVENT.CHANGE, {
        detail: evt,
      })
    );

    window.dispatchEvent(
      new CustomEvent<CustomSelectEvent>(
        `${COMPONENT_TAG}:${CUSTOM_EVENT.CHANGE}`,
        {
          detail: evt,
        }
      )
    );
  }

  updated(changedProperties: Map<string, unknown>) {
    changedProperties.forEach((oldValue, propName) => {
      if (propName !== 'checked') return;
      if (oldValue === this.checked) return;
      this.dispatchChangeEvent();
    });
  }

  handleClick() {
    if (this.readonly || this.disabled) return;
    this.checked = !this.checked;
    // if (this.$node) this.$node.blur();
    // this.dispatchChangeEvent();
  }

  labelMarkup() {
    if (isEmptyString(this.label)) return html``;

    return html`
      <label
        class="fly-checkbox__label"
        for="fly-checkbox-${this.key}-input"
        id="fly-checkbox-${this.key}-label"
        @focus=${() => this.dispatchEvent(new CustomEvent(CUSTOM_EVENT.FOCUS))}
        @blur=${() => this.dispatchEvent(new CustomEvent(CUSTOM_EVENT.BLUR))}
        ?checked=${this.checked}
        ?disabled=${this.disabled}
        ?readonly=${this.readonly}
        ?invalid=${this.invalid}
      >
        ${this.label}
      </label>
    `;
  }

  inputMarkup() {
    const classes = {
      'fly-checkbox__input': true,
      'fly-checkbox__input--invalid': this.invalid,
      'fly-checkbox__input--disabled': this.disabled,
      'fly-checkbox__input--readonly': this.readonly,
      'fly-checkbox__input--checked': this.checked,
    };

    return html`
      <input
        ${ref(this.registerInputNode.bind(this))}
        aria-label=${this.label}
        class=${classMap(classes)}
        type="checkbox"
        name=${this.name}
        ?required=${this.required}
        @focus=${() => this.dispatchEvent(new CustomEvent(CUSTOM_EVENT.FOCUS))}
        @blur=${() => this.dispatchEvent(new CustomEvent(CUSTOM_EVENT.BLUR))}
        ?checked=${this.checked}
        ?disabled=${this.disabled}
        ?readonly=${this.readonly}
        ?invalid=${this.invalid}
        value=${this.value}
        id="fly-checkbox-${this.key}-input"
      />
    `;
  }

  registerInputNode($node: Element | undefined) {
    if (!$node) return;
    this.$node = $node as HTMLInputElement;
  }

  render() {
    return html`
      <div
        id="fly-checkbox-${this.key}"
        class="fly-checkbox"
        @click=${() => this.handleClick()}
      >
        ${this.inputMarkup()} ${this.labelMarkup()}
      </div>
    `;
  }
}

// Register immutable field definition to use with forms.
ImmutableFormRegistry.register<FlyCheckbox, string>(COMPONENT_TAG, {
  getValue(el) {
    return el.value;
  },
  setValue(el, value) {
    el.value = String(value);
  },
  setName(el, name) {
    el.key = String(name);
  },
  reset(el) {
    el.checked = false;
  },
  change(el, trigger) {
    // @ts-expect-error hack
    if (el.formListeningChange) return;
    el.addEventListener('change', () => {
      trigger(this.getValue(el));
    });
    // @ts-expect-error hack
    el.formListeningChange = true;
  },
  focus(el, trigger) {
    // @ts-expect-error hack
    if (el.formListeningFocus) return;
    el.addEventListener('focus', () => trigger());
    el.$node?.addEventListener('focus', () => {
      trigger();
    });
    // @ts-expect-error hack
    el.formListeningFocus = true;
  },
  blur(el, trigger) {
    // @ts-expect-error hack
    if (el.formListeningBlur) return;
    el.addEventListener('blur', () => trigger());
    el.$node?.addEventListener('blur', () => {
      trigger();
    });
    // @ts-expect-error hack
    el.formListeningBlur = true;
  },
});

declare global {
  interface ImmutableFormTypeMap {
    [COMPONENT_TAG]: string;
  }

  interface HTMLElementTagNameMap {
    [COMPONENT_TAG]: FlyCheckbox;
  }
}
